import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Grid, Box, Typography, styled } from "@mui/material"

import i18n from "config/i18n"
import { INSTANCE, BANKS } from "config"
import { useAppDispatch } from "redux/hooks"
import { makePayment } from "redux/features/product_payment/productPaymentThunk"
import Spinner from "components/ui/Spinner"
import Button from "components/ui/Button"
import Title from "components/ui/ModalTitle"
import Form from "components/Products/Payment/Pay/Form"
import BankLinkForm from "components/Products/Payment/Pay/BankLinkForm"
import { formatMoney } from "helpers/formatters"
import { ProductPaymentFormValues, ProductPaymentMethodList } from "types"

type Props = {
  amount: number
  product: string
  paymentMethods: ProductPaymentMethodList
  onToggle: () => void
  onFailure: (error: string) => void
}

const PAYMENT_REQUEST_DEFAULT = {
  VK_SERVICE: "1012",
  VK_VERSION: "008",
  VK_SND_ID: "",
  VK_STAMP: "",
  VK_AMOUNT: "",
  VK_CURR: "EUR",
  VK_REF: "",
  VK_MSG: "",
  VK_RETURN: "",
  VK_CANCEL: "",
  VK_DATETIME: "",
  VK_MAC: "",
  VK_ENCODING: "UTF-8",
  VK_LANG: "ENG",
}

const NewPayment = ({
  amount,
  product,
  paymentMethods,
  onToggle,
  onFailure,
}: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [paymentRequestUri, setPaymentRequestUri] = useState("")
  const [paymentRequestMethod, setPaymentRequestMethod] = useState("POST")
  const [paymentRequestParams, setPaymentRequestParams] = useState(
    PAYMENT_REQUEST_DEFAULT,
  )
  const [isSubmitting, setSubmitting] = useState(false)

  const handleSubmit = (values: ProductPaymentFormValues) => {
    if (isSubmitting) {
      onFailure(i18n.t("common.request_in_progress"))
      return false
    }
    const instance = INSTANCE
    const isBanklink = BANKS.includes(values.bank_name)
    setSubmitting(true)

    const params = {
      instance,
      ...values,
      product,
    }

    dispatch(makePayment(params))
      .then(({ request_uri, request_method, request_params }) => {
        if (isBanklink) {
          setPaymentRequestUri(request_uri)
          setPaymentRequestMethod(request_method)
          setPaymentRequestParams(request_params)
        } else {
          window.location.href = request_uri
        }
      })
      .catch((error) => {
        if (typeof error === "string") {
          onFailure(error) // NOTE: flash message text
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Container>
      <Spinner open={isSubmitting} />
      <Title>{t("features.payment.form.title")}</Title>
      <Wrapper>
        <Amount variant="h1">{formatMoney(amount)}</Amount>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          {paymentMethods.map(({ color, label, value }, index: number) => (
            <Form
              key={index}
              initialValues={{
                amount,
                product,
                bank_name: value,
              }}
              title={t(`banks.${label}.title`)}
              color={color}
              onSubmit={handleSubmit}
            />
          ))}
          {paymentRequestUri && (
            <BankLinkForm
              uri={paymentRequestUri}
              method={paymentRequestMethod}
              params={paymentRequestParams}
            />
          )}
        </Grid>
      </Wrapper>
      <Toggle onClick={onToggle}>
        {t("features.payment.toggle.transfer")}
      </Toggle>
    </Container>
  )
}

export default NewPayment

const Container = styled(Box)(({ theme }) => ({
  marginTop: "40px",
  [theme.breakpoints.down("lg")]: {
    margin: "0",
  },
}))

const Wrapper = styled(Box)`
  margin: 11px 0 80px 0;
`
const Amount = styled(Typography)`
  font-size: 3.5rem;
  font-weight: 400;
  height: 1.4375em;
  line-height: 1.4375em;
  margin: 0;
  padding: 0;
  text-align: center;
`
const Toggle = styled(Button)`
  background: transparent;
  color: ${({ theme }) => theme.palette.primary.main};
  text-align: center;
`
